import { useCallback } from 'react';

import {
  PermissionAccessLevel,
  PermissionName,
} from '@/modules/Settings/types/access-control.const.ts';
import { useGlobalStore } from '@/shared/store/store.ts';

export const useAccessControl = () => {
  const loadingAccessLevels = useGlobalStore(store => store.permissionAccessLevelsLoading);
  const accessLevels = useGlobalStore(store => store.permissionAccessLevels);

  const getPermissionAccessLevel = useCallback(
    (permissionName: PermissionName) => {
      if (loadingAccessLevels) return PermissionAccessLevel.NONE;
      return (
        accessLevels[permissionName] ??
        (permissionName === PermissionName.SETTINGS_MANAGE_ACCESS_CONTROL
          ? PermissionAccessLevel.NONE
          : PermissionAccessLevel.FULL)
      );
    },
    [accessLevels, loadingAccessLevels],
  );

  const hasFullAccess = useCallback(
    (permissionName: PermissionName) => {
      const accessLevel = getPermissionAccessLevel(permissionName);
      return !accessLevel || accessLevel === PermissionAccessLevel.FULL;
    },
    [getPermissionAccessLevel],
  );

  const hasViewAccess = useCallback(
    (permissionName: PermissionName) => {
      const accessLevel = getPermissionAccessLevel(permissionName);
      const viewAccessLevels = [PermissionAccessLevel.FULL, PermissionAccessLevel.VIEW];
      return !accessLevel || viewAccessLevels.includes(accessLevel);
    },
    [getPermissionAccessLevel],
  );

  return { getPermissionAccessLevel, hasFullAccess, hasViewAccess, loadingAccessLevels };
};
